import {AuthorizationProvider} from "@infonova/framework/lib/security/AuthorizationProvider";
import {Logger} from "@infonova/framework/lib/logger/Logger";
import {LoggerFactory} from "@infonova/framework/lib/logger/LoggerFactory";
import {ConfigurableServiceResolver} from "@infonova/framework/lib/service/ConfigurableServiceResolver";
import {FactoryServiceRegistry} from "@infonova/framework/lib/service/factory/FactoryServiceRegistry";
import {LocaleService} from "@infonova/framework/lib/locale/LocaleService";
import {MessageConsumer} from "@infonova/framework/lib/message/types/MessageConsumer";
import {Message} from "@infonova/framework/lib/message/types/Message";
import {ServiceIdentifier} from "@infonova/framework/lib/service/types/ServiceIdentifier";
import {TemplateRepository} from "@infonova/framework/lib/template/TemplateRepository";
import {disable} from "@infonova/framework/lib/cache/ObjectMethodCallCache";
import {SecurityConfiguration} from "@infonova/framework/lib/security/configuration/SecurityConfiguration";
import {ApiConfiguration} from "../configuration/ApiConfiguration";
import {LocaleConfiguration} from "@infonova/framework/lib/locale/configuration/LocaleConfiguration";
import {MessageConfiguration} from "@infonova/framework/lib/message/configuration/MessageConfiguration";
import {TemplateConfiguration} from "@infonova/framework/lib/template/configuration/TemplateConfiguration";
import {FrameworkConfiguration} from "@infonova/framework/lib/configuration/FrameworkConfiguration";
import {TenantServiceIdentifier} from "../configuration/tenant";
import {NotificationMessage} from "@infonova/framework/lib/notification/types/NotificationMessage";
import {RouterMessage} from "@infonova/framework/lib/router/types/RouterMessage";
import {MessageBus} from "@infonova/framework/lib/message/MessageBus";
import {NotificationConfiguration} from "@infonova/framework/lib/notification/configuration/NotificationConfiguration";
import {GlobalNotificationService} from "@infonova/framework/lib/notification/GlobalNotificationService";
import {RouterConfiguration} from "@infonova/framework/lib/router/configuration/RouterConfiguration";
import {RouterService} from "@infonova/framework/lib/router/RouterService";
import {Integration, StartedIntegration} from "./Integration";

const LOGGER: Logger = LoggerFactory.getInstance("Integration");
const requiredServices = [ApiConfiguration.InfonovaApiBasePath, SecurityConfiguration.AuthorizationProvider, TenantServiceIdentifier, LocaleConfiguration.LocaleService];

interface Consumer<T extends Message<unknown>> {
    messageConsumer: MessageConsumer<T>;
    type: string;
}

export class IntegrationImpl implements Integration {
    
    private consumers: Consumer<any>[] = [];
    private onStartHandlers: Array<(infonova: StartedIntegration) => Promise<void> | void> = [];

    private started: boolean = false;

    constructor(readonly serviceResolver: ConfigurableServiceResolver, readonly serviceRegistry: FactoryServiceRegistry
        , private appStartupFunction: () => void
        , private componentStartupFunction: () => void
    ) {
    }

    async start(): Promise<Integration> {
        this.validateNotStarted();
        this.validate();
        let stack = this.onStartHandlers.slice().reverse();
        const executionMethod = (resolve: (value: void | PromiseLike<void>) => void) => {
            const integration = stack.pop();
            if (integration == null) {
                resolve();
            } else {
                resolve(Promise
                    .resolve(integration(this as any))
                    .then(value => new Promise(executionMethod))
                    .catch(value => {
                        LOGGER.error(value);
                        return new Promise(executionMethod);
                    }));
            }
        };

        FrameworkConfiguration.configure();
        this.consumers.forEach(item => {
            this.serviceResolver.getService(MessageConfiguration.MessageBus).subscribe(item.messageConsumer, item.type);
        });
        this.appStartupFunction();
        this.started = true;
        return new Promise(executionMethod).then(() => {
            this.componentStartupFunction();
            return this;
        });
    }


    registerApiBasePath(apiBasePath: string): Integration {
        this.validateNotStarted();
        this.serviceRegistry.registerService(ApiConfiguration.InfonovaApiBasePath, apiBasePath);
        return this;
    }

    registerAuthorizationProvider(authenticationService: AuthorizationProvider): Integration {
        this.validateNotStarted();
        this.serviceRegistry.registerService(SecurityConfiguration.AuthorizationProvider, authenticationService);
        return this;
    }

    registerTenant(tenant: string): Integration {
        this.validateNotStarted();
        this.serviceRegistry.registerService(TenantServiceIdentifier, tenant);
        return this;
    }

    registerMessageConsumer<T extends Message<unknown>>(messageConsumer: MessageConsumer<T>, type: T['type']): Integration {
        this.validateNotStarted();
        this.consumers.push({
            messageConsumer: messageConsumer,
            type: type
        });
        return this;
    }

    registerRouterMessageConsumer(routerMessageConsumer: MessageConsumer<RouterMessage>): Integration {
        this.validateNotStarted();
        return this.registerMessageConsumer(routerMessageConsumer, 'router');
    }

    registerNotificationMessageConsumer(notificationMessageConsumer: MessageConsumer<NotificationMessage>): Integration {
        this.validateNotStarted();
        return this.registerMessageConsumer(notificationMessageConsumer, 'notification');
    }


    registerLocaleService(localeService: LocaleService): Integration {
        this.validateNotStarted();
        this.serviceRegistry.registerService(LocaleConfiguration.LocaleService, localeService);
        return this;
    }

    registerTemplateRepository(templateRepository: TemplateRepository): Integration {
        this.validateNotStarted();
        this.serviceRegistry.registerService(TemplateConfiguration.TemplateRepository, templateRepository);
        return this;
    }

    disableCaching(): Integration {
        disable();
        return this;
    }

    getApiBasePath(): string {
        this.validateStarted();
        return this.serviceResolver.getService(ApiConfiguration.InfonovaApiBasePath);
    }

    getAuthorizationProvider(): AuthorizationProvider {
        this.validateStarted();
        return this.serviceResolver.getService(SecurityConfiguration.AuthorizationProvider);
    }

    getLocaleService(): LocaleService {
        this.validateStarted();
        return this.serviceResolver.getService(LocaleConfiguration.LocaleService);
    }

    getMessageBus(): MessageBus {
        this.validateStarted();
        return this.serviceResolver.getService(MessageConfiguration.MessageBus);
    }

    getTenant(): string {
        this.validateStarted();
        return this.serviceResolver.getService(TenantServiceIdentifier);
    }

    getNotificationService(): GlobalNotificationService {
        this.validateStarted();
        return this.serviceResolver.getService(NotificationConfiguration.GlobalNotificationService);
    }

    getRouterService(): RouterService {
        this.validateStarted();
        return this.serviceResolver.getService(RouterConfiguration.RouterService);
    }

    onStart(handler: (infonova: StartedIntegration) => Promise<void> | void): Integration {
        if (this.started) {
            handler.call(undefined, this as any);
        } else {
            this.onStartHandlers.push(handler);
        }
        return this;
    }

    private checkServiceExists(name: ServiceIdentifier<unknown>): void {
        this.serviceResolver.getService(name);
    }

    private validateNotStarted(): void {
        if (this.started) {
            throw new Error("Invalid operation - Integration must not yet be started");
        }
    }

    private validateStarted(): void {
        if (!this.started) {
            throw new Error("Invalid operation - Integration must be started");
        }
    }

    private validate(): void {
        for (let current of requiredServices) {
            this.checkServiceExists(current);
        }
    }

}